import React, { useState } from 'react';
import { X, Loader } from 'lucide-react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import matomoLogo from '../../logos/matomo.png';
import config from '../../config';

const API_URL = config.API_URL;

/**
 * Le formulaire de connexion à Matomo, par jeton d'API ou par OAuth.
 *
 * ## Pourquoi un composant à part
 *
 * Il vivait dans `ConnectorsView`, à l'intérieur d'`App.js`, et n'était donc atteignable que
 * depuis l'écran Connecteurs. Le parcours d'installation devait pouvoir l'ouvrir aussi ; le
 * recopier aurait créé une seconde version à tenir d'accord avec la première, pour le plus
 * complexe des quatre formulaires — deux méthodes d'authentification, qui écrivent d'ailleurs
 * deux fournisseurs distincts en base (`MATOMO` et `MATOMO_OAUTH`).
 *
 * Le JSX et les gestionnaires sont repris **tels quels**, noms d'états compris : une extraction
 * qui en profite pour réécrire est une extraction dont on ne peut plus dire qu'elle n'a rien
 * changé.
 *
 * ## Deux chemins, deux comportements d'appel
 *
 * - **Jeton d'API** : tout se joue ici, et `onConnecte` est appelé à l'enregistrement.
 * - **OAuth** : la page part chez l'instance Matomo. `onConnecte` n'est jamais appelé — c'est
 *   le retour de redirection qui conclut, chez l'appelant. ⚠️ Le callback Matomo ne lit pas
 *   `returnTo` et ramène à la racine du site : qui ouvre ce formulaire doit donc savoir
 *   retrouver son écran autrement qu'en comptant sur l'URL de retour.
 *
 * Dans les deux cas, Matomo **crée ses comptes à la connexion** : les sites sont importés en
 * bibliothèque dans la foulée, et il n'y a pas d'étape de choix de comptes ensuite.
 *
 * @param {boolean} ouvert
 * @param {() => void} onFermer
 * @param {string} token - JWT de l'utilisateur
 * @param {() => void} onConnecte - après enregistrement réussi (chemin jeton uniquement)
 */
function FormulaireMatomo({ ouvert, onFermer, token, onConnecte }) {
  const [matomoForm, setMatomoForm] = useState({ instanceUrl: '', token: '', authMethod: 'token' });
  const [matomoTesting, setMatomoTesting] = useState(false);
  const [matomoTestResult, setMatomoTestResult] = useState(null);
  const [matomoConnecting, setMatomoConnecting] = useState(false);

  const fermer = () => {
    setMatomoForm({ instanceUrl: '', token: '', authMethod: 'token' });
    setMatomoTestResult(null);
    onFermer?.();
  };

  const handleMatomoTest = async () => {
    if (!matomoForm.instanceUrl || !matomoForm.token) {
      setMatomoTestResult({ success: false, message: 'Veuillez remplir tous les champs' });
      return;
    }

    setMatomoTesting(true);
    setMatomoTestResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/auth/matomo/test`, {
        instanceUrl: matomoForm.instanceUrl,
        token: matomoForm.token
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMatomoTestResult({
          success: true,
          message: response.data.message,
          sites: response.data.sites,
          sitesCount: response.data.sitesCount
        });
      } else {
        setMatomoTestResult({
          success: false,
          message: response.data.error || 'Échec du test'
        });
      }
    } catch (error) {
      console.error('❌ Erreur test Matomo:', error);
      setMatomoTestResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Erreur lors du test'
      });
    } finally {
      setMatomoTesting(false);
    }
  };

  const handleMatomoConnect = async () => {
    if (!matomoTestResult?.success) {
      alert('Veuillez d\'abord tester la connexion');
      return;
    }

    setMatomoConnecting(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/matomo/connect`, {
        instanceUrl: matomoForm.instanceUrl,
        token: matomoForm.token
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        fermer();
        onConnecte?.();
        alert(`✅ ${response.data.message}`);
      } else {
        alert('❌ Erreur : ' + response.data.error);
      }
    } catch (error) {
      console.error('❌ Erreur connexion Matomo:', error);
      alert('❌ Erreur : ' + (error.response?.data?.error || error.message));
    } finally {
      setMatomoConnecting(false);
    }
  };


  // Handler pour OAuth Matomo (PKCE)
  const handleMatomoOAuth = async () => {
    if (!matomoForm.instanceUrl) {
      setMatomoTestResult({ success: false, message: 'Veuillez entrer l\'URL de votre instance Matomo' });
      return;
    }

    try {
      setMatomoConnecting(true);
      const response = await fetch(`${config.API_URL}/api/auth/matomo/oauth/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          instanceUrl: matomoForm.instanceUrl
        })
      });

      const data = await response.json();

      if (data.success && data.authUrl) {
        // Rediriger vers Matomo pour l'authentification
        window.location.href = data.authUrl;
      } else {
        // `hint` porte ce qu'il faut faire, `error` ce qui s'est passé : l'ignorer ne
        // laissait qu'un constat. Les deux tiennent sur une ligne de la modale.
        setMatomoTestResult({
          success: false,
          message: [data.error || 'Erreur lors de l\'initialisation OAuth', data.hint]
            .filter(Boolean)
            .join('\n')
        });
      }
    } catch (error) {
      console.error('Erreur OAuth Matomo:', error);
      setMatomoTestResult({
        success: false,
        message: 'Erreur de connexion au serveur'
      });
    } finally {
      setMatomoConnecting(false);
    }
  };


  if (!ouvert) return null;

  return ReactDOM.createPortal(
        <div onClick={fermer} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e0d5'
            }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.125rem', fontWeight: '600', color: '#1e293b' }}>
                <img src={matomoLogo} alt="Matomo" style={{width: '24px', height: '24px', borderRadius: '4px'}} />
                Connecter une instance Matomo
              </h3>
              <button onClick={fermer} style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#64748b',
                borderRadius: '4px'
              }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              {/* Onglets Token API / OAuth */}
              <div style={{ display: 'flex', marginBottom: '20px', borderBottom: '1px solid #e5e7eb' }}>
                <button
                  onClick={() => { setMatomoForm({ ...matomoForm, authMethod: 'token' }); setMatomoTestResult(null); }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    borderBottom: matomoForm.authMethod === 'token' ? '2px solid #1a1a1a' : '2px solid transparent',
                    color: matomoForm.authMethod === 'token' ? '#1a1a1a' : '#6b7280',
                    fontWeight: matomoForm.authMethod === 'token' ? '600' : '400'
                  }}
                >
                  Token API
                </button>
                <button
                  onClick={() => { setMatomoForm({ ...matomoForm, authMethod: 'oauth' }); setMatomoTestResult(null); }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    borderBottom: matomoForm.authMethod === 'oauth' ? '2px solid #1a1a1a' : '2px solid transparent',
                    color: matomoForm.authMethod === 'oauth' ? '#1a1a1a' : '#6b7280',
                    fontWeight: matomoForm.authMethod === 'oauth' ? '600' : '400'
                  }}
                >
                  OAuth 2.0 (login)
                </button>
              </div>

              {/* Mode Token API */}
              {matomoForm.authMethod === 'token' && (
                <>
                  <p style={{ marginBottom: '20px', color: '#6b7280', fontSize: '0.875rem' }}>
                    Connectez votre instance Matomo en utilisant un Token API.
                    <br />
                    <a href="https://matomo.org/faq/general/faq_114/" target="_blank" rel="noopener noreferrer" style={{ color: '#1a1a1a' }}>
                      Comment obtenir un Token API ?
                    </a>
                  </p>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                      URL de l'instance Matomo
                    </label>
                    <input
                      type="text"
                      value={matomoForm.instanceUrl}
                      onChange={(e) => setMatomoForm({ ...matomoForm, instanceUrl: e.target.value })}
                      placeholder="https://matomo.example.com ou https://example.matomo.cloud"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                      Token API
                    </label>
                    <input
                      type="password"
                      value={matomoForm.token}
                      onChange={(e) => setMatomoForm({ ...matomoForm, token: e.target.value })}
                      placeholder="Votre token API Matomo"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Résultat du test */}
                  {matomoTestResult && (
                    <div style={{
                      padding: '12px',
                      borderRadius: '6px',
                      marginBottom: '16px',
                      backgroundColor: matomoTestResult.success ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${matomoTestResult.success ? '#bbf7d0' : '#fecaca'}`,
                      color: matomoTestResult.success ? '#166534' : '#dc2626'
                    }}>
                      {/* `pre-line` : le message d'échec est structuré en conditions
                          numérotées, qu'un <p> ordinaire recollait en un pavé illisible. */}
                      <p style={{ margin: 0, fontWeight: '500', whiteSpace: 'pre-line' }}>
                        {matomoTestResult.success ? '✓ ' : '✕ '}{matomoTestResult.message}
                      </p>
                      {matomoTestResult.success && matomoTestResult.sites && (
                        <div style={{ marginTop: '12px', fontSize: '0.875rem' }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Sites accessibles :</p>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {matomoTestResult.sites.slice(0, 5).map(site => (
                              <li key={site.idSite}>{site.name}</li>
                            ))}
                            {matomoTestResult.sites.length > 5 && (
                              <li>... et {matomoTestResult.sites.length - 5} autres</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handleMatomoTest}
                      disabled={matomoTesting || !matomoForm.instanceUrl || !matomoForm.token}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        color: '#374151',
                        fontWeight: '500',
                        cursor: matomoTesting || !matomoForm.instanceUrl || !matomoForm.token ? 'not-allowed' : 'pointer',
                        opacity: matomoTesting || !matomoForm.instanceUrl || !matomoForm.token ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {matomoTesting ? (
                        <><Loader className="spin" size={14} /> Test en cours...</>
                      ) : (
                        'Tester la connexion'
                      )}
                    </button>
                    <button
                      onClick={handleMatomoConnect}
                      disabled={matomoConnecting || !matomoTestResult?.success}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: matomoConnecting || !matomoTestResult?.success ? '#9ca3af' : '#1a1a1a',
                        color: 'white',
                        fontWeight: '500',
                        cursor: matomoConnecting || !matomoTestResult?.success ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {matomoConnecting ? (
                        <><Loader className="spin" size={14} /> Connexion...</>
                      ) : (
                        'Connecter'
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Mode OAuth 2.0 */}
              {matomoForm.authMethod === 'oauth' && (
                <>
                  <p style={{ marginBottom: '20px', color: '#6b7280', fontSize: '0.875rem' }}>
                    Connectez-vous avec votre compte Matomo. Vous serez redirigé vers votre instance pour vous authentifier.
                    <br />
                    <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                      Nécessite le plugin OAuth2 sur votre instance Matomo.
                    </span>
                  </p>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                      URL de l'instance Matomo
                    </label>
                    <input
                      type="text"
                      value={matomoForm.instanceUrl}
                      onChange={(e) => setMatomoForm({ ...matomoForm, instanceUrl: e.target.value })}
                      placeholder="https://matomo.example.com ou https://example.matomo.cloud"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Message d'erreur */}
                  {matomoTestResult && !matomoTestResult.success && (
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#dc2626'
                    }}>
                      {/* `pre-line` comme sur l'onglet Token API : le refus OAuth est
                          structuré en conditions numérotées, qu'un <p> ordinaire recolle
                          en un pavé. Ce bloc-ci est propre à l'onglet OAuth — les deux
                          onglets ont chacun le leur, et corriger l'un laissait l'autre. */}
                      <p style={{ margin: 0, fontWeight: '500', whiteSpace: 'pre-line' }}>
                        ✕ {matomoTestResult.message}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleMatomoOAuth}
                    disabled={matomoConnecting || !matomoForm.instanceUrl}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: matomoConnecting || !matomoForm.instanceUrl ? '#9ca3af' : '#1a1a1a',
                      color: 'white',
                      fontWeight: '500',
                      cursor: matomoConnecting || !matomoForm.instanceUrl ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {matomoConnecting ? (
                      <><Loader className="spin" size={14} /> Redirection...</>
                    ) : (
                      'Se connecter avec Matomo'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
    document.body
  );
}

export default FormulaireMatomo;
